import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlay, FaCheckCircle, FaBook, FaSave, FaExternalLinkAlt, FaClock, FaGraduationCap } from 'react-icons/fa';
import { trendingResources } from '../data/trendingResources';
import { useAuth } from '../context/AuthContext';
import { useAlertContext } from '../context/AlertContext';
import { getResourceProgress, updateResourceProgress } from '../services/resourceService';
import ResourceCard from '../components/ResourceCard';

const ResourceDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showAlert } = useAlertContext();

    const [resource, setResource] = useState(null);
    const [progress, setProgress] = useState(null); // { status: "Not Started", notes: [] }
    const [notes, setNotes] = useState([]);
    const [noteInput, setNoteInput] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [relatedResources, setRelatedResources] = useState([]);

    useEffect(() => {
        // Find resource from static data
        const foundResource = trendingResources.find(r => r && r.id === id);
        if (foundResource) {
            setResource(foundResource);
            
            // Find related resources (same category, excluding current)
            const related = trendingResources
                .filter(r => r && r.category === foundResource.category && r.id !== id)
                .slice(0, 3);
            setRelatedResources(related);

            if (user) {
                fetchProgress(id);
            } else {
                setLoading(false);
            }
        } else {
            setLoading(false);
            // Optionally redirect to 404
        }
    }, [id, user]);

    const fetchProgress = async (resourceId) => {
        try {
            const data = await getResourceProgress(resourceId);
            setProgress(data);
            setNotes(data.notes || []);
        } catch (error) {
            console.error("Error fetching progress:", error);
            // If error, assume defaults
            setProgress({ status: "Not Started" });
            setNotes([]);
        } finally {
            setLoading(false);
        }
    };

    const updateProgress = async (newStatus) => {
        if (!user) {
            showAlert({ type: 'info', title: 'Login Required', message: 'Please login to track progress.' });
            return;
        }

        try {
            const data = await updateResourceProgress(id, { status: newStatus });
            setProgress(prev => ({ ...prev, status: data.status }));
            showAlert({ type: 'success', title: 'Success', message: `Marked as ${newStatus}` });
        } catch (error) {
            console.error("Error updating progress:", error);
            const errMsg = error.response?.data?.message || error.message || 'Failed to update progress.';
            showAlert({ 
                type: 'error', 
                title: 'Error', 
                message: errMsg
            });
        }
    };

    const addNote = async () => {
        if (!user) {
             showAlert({ type: 'info', title: 'Login Required', message: 'Please login to save notes.' });
             return;
        }

        if (!noteInput.trim()) return;

        setSaving(true);
        try {
            const data = await updateResourceProgress(id, { note: noteInput });
            
            // Optimistically add note or use response
            if (data.notes) {
                setNotes(data.notes);
            } else {
                 // Fallback
                 setNotes(prev => [...prev, { content: noteInput, createdAt: new Date().toISOString() }]);
            }
            
            setNoteInput("");
            showAlert({ type: 'success', title: 'Saved', message: 'Note added successfully.' });
        } catch (error) {
            console.error("Error saving note:", error);
            const errMsg = error.response?.data?.message || error.message || 'Failed to save note.';
            showAlert({ 
                type: 'error', 
                title: 'Error', 
                message: errMsg
            });
        } finally {
            setSaving(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'text-green-600 bg-green-100 border-green-200';
            case 'In Progress': return 'text-blue-600 bg-blue-100 border-blue-200';
            default: return 'text-gray-600 bg-gray-100 border-gray-200';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (!resource) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
                <h2 className="heading-secondary text-gray-800 mb-4">Resource not found</h2>
                <button 
                  onClick={() => navigate('/resources')}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                >
                    Back to Resources
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-20 pb-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Back Button */}
                <button 
                    onClick={() => navigate(-1)} 
                    className="flex items-center text-gray-600 hover:text-indigo-600 mb-8 transition-colors group"
                >
                    <div className="p-2 rounded-full bg-white shadow-sm border border-gray-200 group-hover:border-indigo-200 mr-2 transition-all">
                        <FaArrowLeft className="w-4 h-4" />
                    </div>
                    Back to Library
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content - Left Column */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Resource Header Card */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="relative h-64 md:h-80 w-full">
                                <img 
                                    src={resource.image || 'https://via.placeholder.com/800x400'} 
                                    alt={resource.title} 
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                                    <div className="p-8 w-full">
                                        <div className="flex flex-wrap gap-3 mb-4">
                                            <span className="bg-white/20 backdrop-blur-md text-white px-3 py-1 rounded-full text-sm font-medium border border-white/30">
                                                {resource.category}
                                            </span>
                                            {resource.rating && (
                                                <span className="bg-yellow-400/20 backdrop-blur-md text-yellow-200 px-3 py-1 rounded-full text-sm font-medium border border-yellow-400/30">
                                                    ★ {resource.rating}
                                                </span>
                                            )}
                                        </div>
                                        <h1 className="heading-primary mb-2 text-white">{resource.title}</h1>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="p-8">
                                <div className="flex flex-col md:flex-row gap-6 justify-between items-start md:items-center mb-8 pb-8 border-b border-gray-100">
                                   <div className="flex-1">
                                       <p className="text-lg text-gray-600 leading-relaxed">
                                          {resource.description}
                                       </p>
                                   </div>
                                    <a 
                                        href={resource.link} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex-shrink-0 inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition shadow-lg hover:shadow-indigo-500/30 transform hover:-translate-y-0.5"
                                    >
                                        Open Resource <FaExternalLinkAlt className="ml-2 w-4 h-4" />
                                    </a>
                                </div>

                                {/* Progress Section */}
                                <div className="mb-0">
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                        <FaClock className="mr-2 text-indigo-500"/> Your Progress
                                    </h3>
                                    
                                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                 <span className={`px-4 py-1.5 rounded-full text-sm font-semibold border ${getStatusColor(progress?.status)}`}>
                                                    {progress?.status || 'Not Started'}
                                                 </span>
                                                 {progress?.startedAt && (
                                                     <span className="text-sm text-gray-500">
                                                         Started: {new Date(progress.startedAt).toLocaleDateString()}
                                                     </span>
                                                 )}
                                            </div>
                                            
                                            <div className="flex gap-3 w-full sm:w-auto">
                                                {progress?.status !== 'In Progress' && progress?.status !== 'Completed' && (
                                                    <button 
                                                        onClick={() => updateProgress('In Progress')}
                                                        className="flex-1 sm:flex-none justify-center px-4 py-2 bg-white border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 font-medium transition flex items-center"
                                                    >
                                                        <FaPlay className="mr-2 w-3 h-3" /> Start Learning
                                                    </button>
                                                )}
                                                
                                                {progress?.status !== 'Completed' && (
                                                    <button 
                                                        onClick={() => updateProgress('Completed')}
                                                        className="flex-1 sm:flex-none justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition shadow-sm flex items-center"
                                                    >
                                                        <FaCheckCircle className="mr-2 w-4 h-4" /> Mark Complete
                                                    </button>
                                                )}

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                         {/* Notes Section - Updated to List */}
                         <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
                            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center justify-between">
                                <div className="flex items-center">
                                    <FaBook className="mr-2 text-indigo-500"/> Your Notes
                                </div>
                                <span className="text-sm font-normal text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{notes.length} Notes</span>
                            </h3>
                            
                            {/* Add Note Input */}
                            <div className="mb-8">
                                <div className="relative">
                                    <textarea 
                                        value={noteInput}
                                        onChange={(e) => setNoteInput(e.target.value)}
                                        placeholder="Write a new note..."
                                        className="w-full h-32 p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-gray-700 placeholder-gray-400 font-medium transition-all"
                                    />
                                    <div className="absolute bottom-4 right-4">
                                        <button 
                                            onClick={addNote}
                                            disabled={saving || !noteInput.trim()}
                                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold text-sm hover:bg-indigo-700 transition flex items-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-indigo-200"
                                        >
                                            <FaSave className="mr-2" /> {saving ? 'Adding...' : 'Add Note'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Notes List */}
                            <div className="space-y-4">
                                {notes && notes.length > 0 ? (
                                    // Sort by newest first
                                    [...notes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((note, index) => (
                                        <div key={index} className="group p-5 bg-yellow-50/50 border border-yellow-100 rounded-2xl hover:border-yellow-200 transition-colors">
                                            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed font-medium">
                                                {note.content}
                                            </p>
                                            <div className="mt-3 flex items-center justify-between text-xs text-gray-400 border-t border-yellow-100/50 pt-3">
                                                <div className='flex items-center gap-1'>
                                                     <FaClock className="w-3 h-3" />
                                                     {new Date(note.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                        <p className="text-gray-400 italic">No notes added yet. Start writing above!</p>
                                    </div>
                                )}
                            </div>
                         </div>
                    </div>

                    {/* Sidebar - Related Resources */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sticky top-24">
                            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                                <FaGraduationCap className="mr-2 text-indigo-500"/> Related Resources
                            </h3>
                            <div className="space-y-4">
                                {relatedResources.length > 0 ? (
                                    relatedResources.map(related => (
                                        <div 
                                            key={related.id} 
                                            onClick={() => navigate(`/resource/${related.id}`)}
                                            className="group cursor-pointer block p-3 rounded-2xl hover:bg-gray-50 transition border border-transparent hover:border-gray-200"
                                        >
                                           <div className="flex gap-3">
                                               <img 
                                                 src={related.image} 
                                                 alt={related.title} 
                                                 className="w-20 h-14 object-cover rounded-lg group-hover:shadow-sm transition"
                                               />
                                               <div>
                                                   <h4 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-indigo-600 transition">
                                                       {related.title}
                                                   </h4>
                                                   <span className="text-xs text-gray-500 mt-1 block">
                                                       {related.category}
                                                   </span>
                                               </div>
                                           </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-gray-500 italic text-sm">No related resources found.</p>
                                )}
                            </div>
                            <button 
                                onClick={() => navigate('/resource-hub')}
                                className="w-full mt-6 py-2.5 text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition"
                            >
                                View All Resources
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResourceDetails;
